import React from 'react'
import { Link } from 'react-router-dom'
import {MapPin, Clock, TrendingUp} from 'lucide-react'

function HipsterDistricts() {
  const districts = [
    {
      id: 1,
      name: 'Shimokitazawa',
      tagline: 'Vintage & Music Haven',
      description: 'Tokyo\'s coolest neighborhood for vintage shopping, indie music venues, and bohemian cafes. A maze of narrow streets filled with character.',
      image: 'https://images.unsplash.com/photo-1590559899731-a382839e5549?w=800&q=80',
      highlights: ['Vintage Shops', 'Live Music', 'Theater Scene', 'Indie Cafes'],
      bestTime: 'Afternoon-Evening',
      link: '/article/shimokitazawa'
    },
    {
      id: 2,
      name: 'Kichijoji',
      tagline: 'Local Living at its Best',
      description: 'Consistently voted Tokyo\'s most liveable neighborhood. Experience authentic local life, amazing food, and the beautiful Inokashira Park.',
      image: 'https://images.unsplash.com/photo-1524413840807-0c3cb6fa808d?w=800&q=80',
      highlights: ['Inokashira Park', 'Food Paradise', 'Local Markets', 'Ghibli Museum Nearby'],
      bestTime: 'All Day',
      link: '/article/kichijoji'
    },
    {
      id: 3,
      name: 'Koenji',
      tagline: 'Retro Vibes & Counter Culture',
      description: 'The epicenter of Tokyo\'s alternative scene. Punk rock heritage, vintage fashion, and the most unique thrift shops in the city.',
      image: 'https://images.unsplash.com/photo-1503899036084-c55cdd92da26?w=800&q=80',
      highlights: ['Thrift Stores', 'Punk Heritage', 'Used Records', 'Izakaya Alley'],
      bestTime: 'Late Afternoon',
      link: '/article/koenji'
    }
  ]

  return (
    <div className="pt-16 min-h-screen bg-gradient-to-b from-[#0A0A0A] to-[#1A1A1A]">
      {/* Header */}
      <section className="relative py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <MapPin className="w-10 h-10 text-[#00E5FF]" />
            <h1 className="text-5xl md:text-6xl font-bold text-white">
              Hipster <span className="text-[#00E5FF]">Districts</span>
            </h1>
          </div>
          <p className="text-xl text-gray-300 max-w-3xl">
            Explore Tokyo's coolest neighborhoods where vintage culture, indie music, and local authenticity thrive. 
            These are the districts where Tokyoites actually hang out.
          </p>
        </div>
      </section>

      {/* Districts Grid */}
      <section className="max-w-7xl mx-auto px-4 pb-20">
        <div className="space-y-12">
          {districts.map((district, index) => (
            <div
              key={district.id}
              className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 bg-[#1A1A1A] rounded-3xl overflow-hidden border-2 border-[#00E5FF]/20 hover:border-[#00E5FF] transition-all group`}
            >
              {/* Image */}
              <div className="lg:w-1/2 relative overflow-hidden">
                <img
                  src={district.image}
                  alt={district.name}
                  className="w-full h-full min-h-[400px] object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
              </div>

              {/* Content */}
              <div className="lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-[#00E5FF]" />
                  <span className="text-[#00E5FF] font-bold text-sm uppercase tracking-wider">
                    Popular Choice
                  </span>
                </div>

                <h2 className="text-4xl font-bold text-white mb-3">
                  {district.name}
                </h2>
                <p className="text-[#00E5FF] text-xl font-semibold mb-4">
                  {district.tagline}
                </p>
                <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                  {district.description}
                </p>

                {/* Highlights */}
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2">
                    {district.highlights.map((highlight, idx) => (
                      <span
                        key={idx}
                        className="px-4 py-2 bg-[#00E5FF]/10 text-[#00E5FF] rounded-full text-sm font-medium border border-[#00E5FF]/30"
                      >
                        {highlight}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Best Time */}
                <div className="flex items-center gap-2 mb-8 text-gray-400">
                  <Clock className="w-5 h-5" />
                  <span>Best Time: <span className="text-white font-medium">{district.bestTime}</span></span>
                </div>

                {/* CTA Button */}
                <Link
                  to={district.link}
                  className="inline-block px-8 py-4 bg-gradient-to-r from-[#00E5FF] to-[#00B8CC] text-white font-bold rounded-full hover:shadow-lg hover:shadow-[#00E5FF]/50 transition-all hover:scale-105 text-center"
                  style={{ boxShadow: '0 0 30px rgba(0, 229, 255, 0.3)' }}
                >
                  Explore {district.name} →
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}

export default HipsterDistricts
